import find from 'lodash/find';
import get from 'lodash/get';
import includes from 'lodash/includes';
import indexOf from 'lodash/indexOf';
import some from 'lodash/some';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import loadable from '@loadable/component';
import {connect} from 'react-redux';
import {compose} from 'redux';
import {Helmet} from 'react-helmet-async';
import moment from 'moment-timezone';
import Button from '@spothero/ui/button';
import AnimationUtils from '@spothero/utils/animation';
import DOMUtils from '@spothero/utils/dom';
import StorageUtils from '@spothero/utils/storage';
import GTMUtils from 'utils/gtm';
import SHUtils from 'utils/sh-utils';
import trackAvailabilityCheck from 'segment/events/availability-check';
import CreditCardAPI from 'api/credit-card';
import withConfig from 'config/withConfig';
import SHLoader from 'common/loader/SHLoader';
import Card from 'common/card/Card';
import PurchasingOverlay from './common/PurchasingOverlay';
import {CHECKOUT_ERROR_DATA} from 'checkout/checkout-prop-types';
import {USER_DATA} from 'user/user-prop-types';
import {push} from 'router/router-actions';
import trackCheckoutStepCompleted from 'segment/events/checkout-step-completed';
import {userUpdate} from 'user/user-actions';
import UserUtils from 'user/user-utils';
import SegmentUtils from 'utils/segment';
import {
    clearCustomer,
    fetchRates,
    showChangeDateTime,
    hideChangeDateTime,
    hideRecommendedSpots,
    hideRedundantMonthlyDetails,
    toggleTimeExtendedDetails,
    updateData,
    purchase,
} from './checkout-actions';
import {
    hideRateDetails,
    toggleOnlineCommuterDetails,
    toggleOversizedFeeDetails,
    resetSelectedRate,
} from 'spot/spot-actions';
import CheckoutMeta from './CheckoutMeta';
import AccountInformation from './account/AccountInformation';
import ReservationDetails from './reservation/ReservationDetails';
import PaymentInformation from './payment/PaymentInformation';
import ReservationSummary from './summary/ReservationSummary';
import CheckoutValueProps from './common/CheckoutValueProps';

const WhyTimeExtendedModal = loadable(() =>
    import('common/extended-time/WhyTimeExtendedModal')
);
const RateDetailsModal = loadable(() =>
    import('spot-details/modals/RateDetailsModal')
);
const OnlineCommuterRateModal = loadable(() =>
    import('spot-details/modals/OnlineCommuterRateModal')
);
const OversizedFeeDetailsModal = loadable(() =>
    import('spot-details/modals/OversizedFeeDetailsModal')
);
const ChangeDateTimeModal = loadable(() => import('./ChangeDateTimeModal'));
const RecommendedSpotsModal = loadable(() => import('./RecommendedSpotsModal'));
const RedundantMonthlyReservationModal = loadable(() =>
    import('./RedundantMonthlyReservationModal')
);

class Checkout extends Component {
    static propTypes = {
        isLoadingRates: PropTypes.bool.isRequired,
        isPurchasing: PropTypes.bool.isRequired,
        isPurchasingWithPaymentRequest: PropTypes.bool,
        apiDateTimeFormat: PropTypes.string.isRequired,
        checkoutError: PropTypes.shape(CHECKOUT_ERROR_DATA),
        user: PropTypes.shape(USER_DATA).isRequired,
        selectedCreditCard: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
        ]),
        match: PropTypes.shape({
            params: PropTypes.shape({
                spotId: PropTypes.string,
            }),
        }),
        spot: PropTypes.object,
        isMonthly: PropTypes.bool,
        isAirport: PropTypes.bool,
        rateSessionId: PropTypes.string,
        starts: PropTypes.string,
        ends: PropTypes.string,
        latitude: PropTypes.number,
        longitude: PropTypes.number,
        rid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        eventId: PropTypes.number,
        phoneNumber: PropTypes.string,
        phoneNumberFriendly: PropTypes.string,
        monthlyPhoneNumber: PropTypes.string,
        monthlyPhoneNumberFriendly: PropTypes.string,
        citySlug: PropTypes.string,
        cityState: PropTypes.string,
        cityDisplayName: PropTypes.string,
        cityTimezone: PropTypes.string,
        previousRental: PropTypes.bool,
        showChangeDateTimeModal: PropTypes.bool,
        recommendedSpotsUnavailableReason: PropTypes.string,
        showRecommendedSpotsModal: PropTypes.bool,
        showRedundantMonthlyReservationModal: PropTypes.bool,
        redundantMonthlyReservationModalDate: PropTypes.string,
        redundantMonthlyReservationModalParkingPassURL: PropTypes.string,
        showRateDetailsModal: PropTypes.bool,
        rateDetailsModalSection: PropTypes.string,
        showOversizedFeeDetailsModal: PropTypes.bool,
        showOnlineCommuterRateModal: PropTypes.bool,
        showWhyTimeExtendedModal: PropTypes.bool,
        purchaseForCustomer: PropTypes.bool,
        isRebookingReservation: PropTypes.bool,
        changeDateTimeModalTitle: PropTypes.string,
        changeDateTimeModalIntroMessage: PropTypes.string,
        onHideDateTimeModal: PropTypes.func.isRequired,
        fetchSpotRates: PropTypes.func.isRequired,
        clearSelectedRate: PropTypes.func.isRequired,
        onClearCustomer: PropTypes.func.isRequired,
        onUserUpdate: PropTypes.func.isRequired,
        onUpdateData: PropTypes.func.isRequired,
        onShowDateTimeModal: PropTypes.func.isRequired,
        onHideRecommendedSpotsModal: PropTypes.func.isRequired,
        onHideRedundantMonthlyDetailsModal: PropTypes.func.isRequired,
        onHideRateDetailsModal: PropTypes.func.isRequired,
        onToggleOnlineCommuterDetailsModal: PropTypes.func.isRequired,
        onToggleOversizedFeeDetailsModal: PropTypes.func.isRequired,
        onToggleTimeExtendedDetailsModal: PropTypes.func.isRequired,
        onPurchase: PropTypes.func.isRequired,
        pushTo: PropTypes.func.isRequired,
        changedDateTimeCount: PropTypes.number.isRequired,
        vehicleProfileId: PropTypes.number,
        vehicleInfoId: PropTypes.number,
    };
    state = {
        isFirstView: true,
        prevUser: this.props.user,
        vehicleMakeModelPlate: '',
    };

    constructor(props) {
        super(props);

        const {
            match: {
                params: {spotId},
            },
        } = props;

        this._lastActionType = 'ENTERED_CHECKOUT';
        this._userUnsubmitted = false;
        this._firstLoad = true;
        this._spotId = parseInt(spotId, 10);
        this._bodyClasses = SHUtils.determineInitialLoadBodyClasses({
            pageType: 'checkout',
        });
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        const {user} = nextProps;
        const {prevUser} = prevState;

        if (prevUser.email !== user.email) {
            return {
                highlightAccountPanel: true,
                prevUser: user,
            };
        }

        return null;
    }

    componentDidMount() {
        const {
            apiDateTimeFormat,
            isMonthly,
            isAirport,
            starts,
            ends,
            rid,
            eventId,
            previousRental,
            onShowDateTimeModal,
            fetchSpotRates,
            onUpdateData,
            vehicleProfileId,
            vehicleInfoId,
        } = this.props;

        this._checkoutInitMoment = moment();

        const vehicleToCheck = vehicleProfileId || vehicleInfoId || null;

        fetchSpotRates({
            spotId: this._spotId,
            starts,
            ends: isMonthly
                ? moment(starts)
                      .add(1, 'month')
                      .format(apiDateTimeFormat)
                : ends,
            eventId,
            includeFacility: true,
            monthly: isMonthly,
            airport: isAirport,
            meta: {
                rid: parseInt(rid, 10),
                previousRental,
            },
            vehicleToCheck,
        })
            .then(response => {
                if (previousRental) {
                    onShowDateTimeModal({
                        title: 'Buy this Spot Again',
                    });
                }
            })
            .catch(error => {
                console.log('Checkout fetchSpotRates error:', error); // eslint-disable-line no-console
            });

        SegmentUtils.track({
            event: 'entered_checkout',
        });

        onUpdateData({
            changedDateTimeCount: 0,
        });

        GTMUtils.push({
            event: 'pageview',
            page: 'checkout',
            spotId: this._spotId,
        });

        SHUtils.staticScrollTo();
    }

    componentDidUpdate(prevProps, prevState) {
        const {
            isMonthly,
            user,
            checkoutError,
            rateSessionId,
            selectedCreditCard,
            onUserUpdate,
            onUpdateData,
            onShowDateTimeModal,
            citySlug,
            cityState,
            vehicleProfileId,
            vehicleInfoId,
            isAirport,
            starts,
            ends,
            rid,
            spot,
            eventId,
            fetchSpotRates,
            apiDateTimeFormat,
        } = this.props;
        const {
            rateSessionId: prevRateSessionId,
            isMonthly: prevMonthly,
            user: prevUser,
            checkoutError: prevCheckoutError,
            vehicleProfileId: prevVehicleProfileId,
            vehicleInfoId: prevVehicleInfoId,
            spot: prevSpot,
        } = prevProps;
        const rateInfo = {
            spotId: this._spotId,
            starts,
            ends: isMonthly
                ? moment(starts)
                      .add(1, 'month')
                      .format(apiDateTimeFormat)
                : ends,
            eventId,
            includeFacility: true,
            monthly: isMonthly,
            airport: isAirport,
            meta: {
                rid: parseInt(rid, 10),
            },
        };

        if (vehicleProfileId) {
            rateInfo.vehicleProfileId = vehicleProfileId;
        } else if (vehicleInfoId) {
            rateInfo.vehicleInfoId = vehicleInfoId;
        }

        if (
            (!prevSpot && spot) ||
            (spot && prevSpot && spot.selectedRate !== prevSpot.selectedRate)
        ) {
            this._firstLoad = false;
        }

        if (
            prevVehicleProfileId !== vehicleProfileId ||
            prevVehicleInfoId !== vehicleInfoId
        ) {
            this._lastActionType = 'UPDATED_VEHICLE';

            fetchSpotRates(rateInfo).catch(error => {
                // eslint-disable-next-line no-console
                console.log('Checkout fetchSpotRates() error:', error);
            });
        }

        if (prevMonthly !== isMonthly) {
            AnimationUtils.scrollTo(document.body, 0);

            if (!isMonthly) {
                onShowDateTimeModal();
            }
        }

        if (
            prevState.highlightAccountPanel !== this.state.highlightAccountPanel
        ) {
            this._clearHighlightTimeout();
            this._highlightTimeout = setTimeout(() => {
                this.setState({
                    highlightAccountPanel: false,
                });
            }, 3000);
        }

        if (!prevCheckoutError && checkoutError) {
            if (checkoutError.errors.length) {
                if (
                    some(checkoutError.errors, item => includes(item, 'card'))
                ) {
                    const declinedCard = find(user.creditCards, [
                        'id',
                        selectedCreditCard,
                    ]);

                    if (declinedCard) {
                        const declinedCardIndex = indexOf(
                            user.creditCards,
                            declinedCard
                        );

                        declinedCard.disabled = true;
                        declinedCard.isDefault = false;

                        user.creditCards.splice(
                            declinedCardIndex,
                            1,
                            declinedCard
                        );

                        CreditCardAPI.setAsDefault({
                            userId: user.id,
                            cardId: selectedCreditCard,
                            isDefault: false,
                        }).catch(error => {
                            // eslint-disable-next-line no-console
                            console.log(
                                'Card not found, could not update default status.'
                            );
                        });
                    }

                    onUserUpdate(user);
                    onUpdateData({
                        selectedCreditCard: 'new',
                    });
                }
            }
        }

        if (prevRateSessionId !== rateSessionId && rateSessionId) {
            // a new rate load was initiated which signifies an availability check
            trackAvailabilityCheck({
                searchRequest: {
                    monthly: isMonthly,
                    starts,
                    ends,
                },
                city: {
                    slug: citySlug,
                    state: cityState,
                },
                // eslint-disable-next-line camelcase
                ...(eventId && {event: {event_id: eventId}}),
                spot,
                userStatus: user.status,
                screen: 'checkout',
                actionType: this._lastActionType,
            });
        }

        // this._userUnsubmitted indicates Pay and Reserve was clicked with unsubmitted user/guest login info
        // in this case, the first time _purchase runs the user will be logged in only, without reserving
        // so we programatically call _purchase a second time to ensure the reservation process runs
        if (this._userUnsubmitted && user.email !== prevUser.email) {
            this._userUnsubmitted = false;

            setTimeout(() => {
                // delay this slightly so that the data has a chance to update after a login is executed
                // before trying to re-purchase or else it will try to log the user in again and fail
                this._purchase();
            }, 100);
        }
    }

    componentWillUnmount() {
        const {onClearCustomer, clearSelectedRate} = this.props;

        this._clearHighlightTimeout();

        onClearCustomer();
        clearSelectedRate();
    }

    _onBackToSearchClick = evt => {
        evt.preventDefault();

        const {latitude, longitude, starts, ends, eventId, pushTo} = this.props;
        const previousSearch = StorageUtils.get('previous-search', 'session');
        let url = `/search?starts=${starts}&ends=${ends}`;

        if (latitude && longitude) {
            url += `&latitude=${latitude}&longitude`;
        }

        if (eventId) {
            url += `&eid=${eventId}`;
        }

        if (previousSearch) {
            StorageUtils.remove('previous-search', 'session');

            url = previousSearch.url;
        }

        pushTo(url);
    };

    _onReservationDetailsContinue = () => {
        this.setState({
            isFirstView: false,
        });
    };

    _onReserveClick = (paymentRequestData = null) => {
        // paymentRequestData is used for Apple/Google Pay and contains the paymentOverlay (which has the token.id)
        // and stripePaymentType otherwise will be null for regular purchase
        this._purchase(paymentRequestData);
    };

    _onVehicleChange = ({value, label}) => {
        const {onUpdateData, user} = this.props;
        const vehicleMakeModelPlate = label;
        const vehicleIdentifier =
            value !== 'new' && value !== 'DONTKNOW' && value !== 'unlisted'
                ? parseInt(value, 10)
                : null;

        if (vehicleMakeModelPlate) {
            this.setState({
                vehicleMakeModelPlate,
            });
        }

        const vehicleData = {};

        if (vehicleIdentifier) {
            if (user.id) {
                vehicleData.vehicleProfileId = vehicleIdentifier;
                vehicleData.vehicleInfoId = null;
            } else {
                vehicleData.vehicleInfoId = vehicleIdentifier;
                vehicleData.vehicleProfileId = null;
            }
        } else {
            vehicleData.vehicleProfileId = null;
            vehicleData.vehicleInfoId = null;
        }

        onUpdateData(vehicleData);
    };

    _onChangeDateTimeModalChange = ({start, end, meta}) => {
        const {
            apiDateTimeFormat,
            isMonthly,
            isAirport,
            previousRental,
            fetchSpotRates,
            vehicleProfileId,
        } = this.props;
        const starts = start.format(apiDateTimeFormat);
        const ends = isMonthly
            ? start
                  .clone()
                  .add(1, 'month')
                  .format(apiDateTimeFormat)
            : end.format(apiDateTimeFormat);

        this._lastActionType = previousRental
            ? 'QUICK_REBOOK'
            : 'UPDATE_REQUESTED_TIMES';

        fetchSpotRates({
            spotId: this._spotId,
            includeFacility: true,
            starts,
            ends,
            monthly: isMonthly,
            airport: isAirport,
            meta: {
                previousRental,
                dateTimeChanged: true,
            },
            vehicleProfileId,
        }).catch(error => {
            console.log('fetchSpotRates error:', error); // eslint-disable-line no-console
        });
    };

    _onCompleteRedundantMonthlyReservation = () => {
        this._purchase({
            forceRedundantMonthly: true,
        });
    };

    _onReservationSuccess = (checkoutData, reservation) => {
        const {
            isAirport,
            changedDateTimeCount,
            starts: searchStarts,
            ends: searchEnds,
        } = this.props;

        trackCheckoutStepCompleted({
            checkoutData,
            reservation,
            isAirport,
            changedDateTimeCount,
            searchStarts,
            searchEnds,
            initMoment: this._checkoutInitMoment,
        });
    };

    _clearHighlightTimeout() {
        if (this._highlightTimeout) {
            clearTimeout(this._highlightTimeout);
        }
    }

    _submitReservationDetails = async () => {
        const data = {};

        if (this._reservationDetails) {
            data.reservation = await this._reservationDetails.submit();

            if (!data.reservation) {
                AnimationUtils.scrollTo(
                    'html',
                    DOMUtils.getDocumentOffset('.CheckoutPanel-reservation').top
                );
            }
        }

        return data;
    };

    async _submitForms() {
        const formData = await this._submitReservationDetails();

        if (this._paymentInformation) {
            formData.payment = this._paymentInformation.submit();
        }

        return formData;
    }

    async _purchase({
        // the payment overlay is passed through if Apple or Google Pay is used, it contains token.id and complete('success/fail')
        paymentOverlay = null,
        // again, if Apple or Google Pay, Stripe payment type is passed through based on which is used
        stripePaymentType = 'cc',
        forceRedundantMonthly = false,
    } = {}) {
        const {
            user: {isAdmin, status},
            onPurchase,
            purchaseForCustomer,
            isRebookingReservation,
        } = this.props;
        const {reservation, payment} = await this._submitForms();

        if (status === UserUtils.AUTH_STATE.LOGGED_OUT) {
            this._userUnsubmitted = true;
        }

        if (this._reservationDetails && !reservation) {
            return;
        } else if (
            this._paymentInformation &&
            !payment &&
            !purchaseForCustomer &&
            !isRebookingReservation
        ) {
            AnimationUtils.scrollTo(
                'html',
                DOMUtils.getDocumentOffset('.CheckoutPanel-payment').top
            );
        } else {
            onPurchase({
                reservationData: {
                    ...reservation,
                    ...payment,
                    paymentOverlay,
                    stripePaymentType,
                    forceRedundantMonthly,
                },
                isAdmin,
                onSuccess: this._onReservationSuccess,
            });
        }
    }

    _renderUnavailable() {
        return (
            <Card className="Checkout-spot-information-unavailable">
                <p>
                    No spots are available at this time but we don&apos;t want
                    you going spotless...
                </p>
                <Button
                    className="Button-space-after"
                    color="primary"
                    onClick={this.props.onShowDateTimeModal}
                >
                    Edit Date/Time
                </Button>
                <span>or</span>
                <Button
                    className="Button-space-before"
                    color="secondary"
                    onClick={this._onBackToSearchClick}
                >
                    Return to Search
                </Button>
            </Card>
        );
    }

    render() {
        const {
            user: {isAdmin},
            isLoadingRates,
            isPurchasing,
            isPurchasingWithPaymentRequest,
            spot,
            starts,
            ends,
            isMonthly,
            phoneNumber,
            phoneNumberFriendly,
            monthlyPhoneNumber,
            monthlyPhoneNumberFriendly,
            cityDisplayName,
            cityTimezone,
            showChangeDateTimeModal,
            recommendedSpotsUnavailableReason,
            showRecommendedSpotsModal,
            onHideRecommendedSpotsModal,
            showRedundantMonthlyReservationModal,
            onHideRedundantMonthlyDetailsModal,
            redundantMonthlyReservationModalDate,
            redundantMonthlyReservationModalParkingPassURL,
            showRateDetailsModal,
            rateDetailsModalSection,
            showOnlineCommuterRateModal,
            showOversizedFeeDetailsModal,
            showWhyTimeExtendedModal,
            fetchSpotRates,
            onHideRateDetailsModal,
            onToggleOnlineCommuterDetailsModal,
            onToggleOversizedFeeDetailsModal,
            onToggleTimeExtendedDetailsModal,
            changeDateTimeModalTitle,
            changeDateTimeModalIntroMessage,
            onHideDateTimeModal,
        } = this.props;
        const {isFirstView, vehicleMakeModelPlate} = this.state;

        if (!spot) {
            return <SHLoader className="Checkout-loader" />;
        }

        const cancellationAllowed =
            get(spot, 'facility.cancellation_allowed') !== 'no';

        return (
            <div className="Checkout">
                <Helmet>
                    <body className={this._bodyClasses} />
                </Helmet>
                <CheckoutMeta cityDisplayName={cityDisplayName} spot={spot} />
                <div className="container">
                    {isLoadingRates && this._firstLoad ? (
                        <SHLoader className="Checkout-loader" />
                    ) : (
                        <>
                            <div className="Checkout-spot-information">
                                {spot.selectedRate ? (
                                    <ReservationSummary
                                        isLoadingRates={isLoadingRates}
                                    />
                                ) : (
                                    this._renderUnavailable()
                                )}
                                <CheckoutValueProps
                                    className="CheckoutValueProps-sidebar"
                                    showCancellationValueProp={
                                        cancellationAllowed
                                    }
                                />
                                <CheckoutValueProps
                                    className="CheckoutValueProps-footer"
                                    showCancellationValueProp={
                                        cancellationAllowed
                                    }
                                />
                            </div>
                            <div className="Checkout-reservation CheckoutPanel-group">
                                <AccountInformation
                                    ref={node => {
                                        this._accountInformation = node;
                                    }}
                                />
                                <ReservationDetails
                                    ref={node => {
                                        this._reservationDetails = node;
                                    }}
                                    isFirstView={isFirstView}
                                    onContinue={
                                        this._onReservationDetailsContinue
                                    }
                                    onVehicleChange={this._onVehicleChange}
                                    isLoadingRates={isLoadingRates}
                                    vehicleMakeModelPlate={
                                        vehicleMakeModelPlate
                                    }
                                />
                                <PaymentInformation
                                    ref={node => {
                                        this._paymentInformation = node;
                                    }}
                                    isFirstView={isFirstView}
                                    isLoadingRates={isLoadingRates}
                                    validateReservationInfo={
                                        this._submitReservationDetails
                                    }
                                    onReserveClick={this._onReserveClick}
                                />
                            </div>
                        </>
                    )}
                </div>
                {showChangeDateTimeModal && (
                    <ChangeDateTimeModal
                        isAdmin={isAdmin}
                        timezone={cityTimezone}
                        title={changeDateTimeModalTitle}
                        introMessage={changeDateTimeModalIntroMessage}
                        isMonthly={isMonthly}
                        starts={starts}
                        ends={ends}
                        showOtherOptions={!spot.selectedRate}
                        onChange={this._onChangeDateTimeModalChange}
                        onHidden={onHideDateTimeModal}
                    />
                )}
                {showRateDetailsModal && (
                    <RateDetailsModal
                        section={rateDetailsModalSection}
                        spot={spot}
                        onHidden={onHideRateDetailsModal}
                    />
                )}
                {showOnlineCommuterRateModal && (
                    <OnlineCommuterRateModal
                        description={
                            spot.selectedRate.online_commuter_rate_description
                        }
                        onHidden={onToggleOnlineCommuterDetailsModal}
                    />
                )}
                {showOversizedFeeDetailsModal && (
                    <OversizedFeeDetailsModal
                        spot={spot}
                        onHidden={onToggleOversizedFeeDetailsModal}
                    />
                )}
                {showWhyTimeExtendedModal && (
                    <WhyTimeExtendedModal
                        requestedStart={starts}
                        requestedEnd={ends}
                        rate={spot.selectedRate}
                        onHidden={onToggleTimeExtendedDetailsModal}
                    />
                )}
                {showRecommendedSpotsModal && (
                    <RecommendedSpotsModal
                        isAdmin={isAdmin}
                        latitude={spot.latitude}
                        longitude={spot.longitude}
                        starts={starts}
                        ends={ends}
                        isMonthly={isMonthly}
                        timezone={cityTimezone}
                        currentSpotId={spot.spotId}
                        recommendedSpotsUnavailableReason={
                            recommendedSpotsUnavailableReason
                        }
                        onHidden={onHideRecommendedSpotsModal}
                        onBackToSearchClick={this._onBackToSearchClick}
                        fetchSpotRates={fetchSpotRates}
                    />
                )}
                {showRedundantMonthlyReservationModal && (
                    <RedundantMonthlyReservationModal
                        onHidden={onHideRedundantMonthlyDetailsModal}
                        reservationCreationDateFormatted={
                            redundantMonthlyReservationModalDate
                        }
                        parkingPassURL={
                            redundantMonthlyReservationModalParkingPassURL
                        }
                        contactNumber={monthlyPhoneNumber || phoneNumber}
                        contactNumberFriendly={
                            monthlyPhoneNumberFriendly || phoneNumberFriendly
                        }
                        onCompleteReservation={
                            this._onCompleteRedundantMonthlyReservation
                        }
                    />
                )}
                {isPurchasing && (
                    <PurchasingOverlay
                        showContent={!isPurchasingWithPaymentRequest}
                    />
                )}
            </div>
        );
    }
}

const mapStateToProps = state => {
    const {
        checkout: {
            rateSessionId,
            isPurchasing,
            isFetchingRates,
            checkoutError,
            rid,
            previousRental,
            recommendedSpotsUnavailableReason,
            showChangeDateTimeModal,
            changeDateTimeModalTitle,
            changeDateTimeModalIntroMessage,
            showRecommendedSpotsModal,
            showRedundantMonthlyReservationModal,
            redundantMonthlyReservationModalDate,
            redundantMonthlyReservationModalParkingPassURL,
            showWhyTimeExtendedModal,
            purchaseForCustomer,
            isRebookingReservation,
            changedDateTimeCount,
            vehicleInfoId,
            vehicleProfileId,
            isPurchasingWithPaymentRequest,
        },
        city: {
            data: {
                slug: citySlug,
                state: cityState,
                phone_number: phoneNumber,
                phone_number_friendly: phoneNumberFriendly,
                monthly_phone_number: monthlyPhoneNumber,
                monthly_phone_number_friendly: monthlyPhoneNumberFriendly,
                display_name: cityDisplayName,
                timezone,
            },
        },
        searchRequest: {
            monthly,
            airport,
            starts,
            ends,
            latitude,
            longitude,
            eid,
        },
        spot: {
            selected,
            showRateDetailsModal,
            rateDetailsModalSection,
            showOversizedFeeDetailsModal,
            showOnlineCommuterRateModal,
        },
        user: {data: user},
    } = state;

    return {
        isLoadingRates: isFetchingRates,
        isPurchasing,
        rateSessionId,
        user,
        checkoutError,
        isMonthly: monthly,
        isAirport: airport,
        starts,
        ends,
        latitude,
        longitude,
        eventId: eid,
        rid,
        previousRental,
        spot: selected,
        showChangeDateTimeModal,
        changeDateTimeModalTitle,
        changeDateTimeModalIntroMessage,
        showRateDetailsModal,
        rateDetailsModalSection,
        showOversizedFeeDetailsModal,
        showOnlineCommuterRateModal,
        citySlug,
        cityState,
        phoneNumber,
        phoneNumberFriendly,
        monthlyPhoneNumber,
        monthlyPhoneNumberFriendly,
        cityDisplayName,
        cityTimezone: timezone,
        recommendedSpotsUnavailableReason,
        showRecommendedSpotsModal,
        showRedundantMonthlyReservationModal,
        redundantMonthlyReservationModalDate,
        redundantMonthlyReservationModalParkingPassURL,
        showWhyTimeExtendedModal,
        purchaseForCustomer,
        isRebookingReservation,
        changedDateTimeCount,
        vehicleInfoId,
        vehicleProfileId,
        isPurchasingWithPaymentRequest,
    };
};

const mapDispatchToProps = {
    fetchSpotRates: fetchRates,
    clearSelectedRate: resetSelectedRate,
    onHideDateTimeModal: hideChangeDateTime,
    onShowDateTimeModal: showChangeDateTime,
    onHideRecommendedSpotsModal: hideRecommendedSpots,
    onHideRedundantMonthlyDetailsModal: hideRedundantMonthlyDetails,
    onHideRateDetailsModal: hideRateDetails,
    onToggleOnlineCommuterDetailsModal: toggleOnlineCommuterDetails,
    onToggleOversizedFeeDetailsModal: toggleOversizedFeeDetails,
    onToggleTimeExtendedDetailsModal: toggleTimeExtendedDetails,
    onClearCustomer: clearCustomer,
    onUserUpdate: userUpdate,
    onUpdateData: updateData,
    onPurchase: purchase,
    pushTo: push,
};

const enhance = compose(
    withConfig(['apiDateTimeFormat']),
    connect(
        mapStateToProps,
        mapDispatchToProps
    )
);

export default enhance(Checkout);
